import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

const formatDate = (iso) => {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  })
}

export default function Notes({ enfantId, semaine, estEcole, estParent2 = false, pseudonyme = '' }) {
  // Fil unique : 'parent' (parent1 + parent2) ↔ 'ecole'
  // Parent2 peut lire et écrire côté parent
  const auteur = estEcole ? 'ecole' : 'parent'
  const autreAuteur = estEcole ? 'parent' : 'ecole'
  const [maNote, setMaNote] = useState('')
  const [noteAutre, setNoteAutre] = useState('')
  const [maDateMaj, setMaDateMaj] = useState(null)
  const [autreDateMaj, setAutreDateMaj] = useState(null)
  const [maNoteEnvoyee, setMaNoteEnvoyee] = useState('') // version sauvegardée
  const [saving, setSaving] = useState(false)
  const [enModeEdition, setEnModeEdition] = useState(false)

  useEffect(() => {
    const charger = async () => {
      const { data } = await supabase
        .from('notes').select('auteur, contenu, updated_at')
        .eq('enfant_id', enfantId).eq('semaine', semaine)
      if (data) {
        const mienne = data.find(n => n.auteur === auteur)
        const autre = data.find(n => n.auteur === autreAuteur)
        setMaNote(mienne?.contenu || '')
        setMaNoteEnvoyee(mienne?.contenu || '')
        setMaDateMaj(mienne?.updated_at || null)
        setNoteAutre(autre?.contenu || '')
        setAutreDateMaj(autre?.updated_at || null)
        setEnModeEdition(!mienne?.contenu)
      } else {
        setEnModeEdition(true)
      }
    }
    charger()
  }, [enfantId, semaine, auteur, autreAuteur])

  const sauvegarder = async () => {
    if (!maNote.trim()) return
    setSaving(true)
    const now = new Date().toISOString()
    await supabase.from('notes').upsert({
      enfant_id: enfantId, semaine, auteur,
      contenu: maNote.trim(), updated_at: now
    }, { onConflict: 'enfant_id,semaine,auteur' })
    setMaNoteEnvoyee(maNote.trim())
    setMaDateMaj(now)
    setSaving(false)
    setEnModeEdition(false)
  }

  const imprimer = () => {
    const notesParent = auteur === 'parent' ? maNoteEnvoyee : noteAutre
    const notesEcole = auteur === 'ecole' ? maNoteEnvoyee : noteAutre
    const dateParent = auteur === 'parent' ? maDateMaj : autreDateMaj
    const dateEcole = auteur === 'ecole' ? maDateMaj : autreDateMaj

    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
    <title>Notes — ${pseudonyme}</title>
    <style>
      body { font-family: Georgia, serif; padding: 32px; color: #1e293b; max-width: 600px; margin: 0 auto; }
      h1 { font-size: 22px; margin-bottom: 4px; }
      .sub { color: #64748b; font-size: 13px; margin-bottom: 28px; }
      .bloc { border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px; margin-bottom: 20px; background: #f8fafc; }
      .label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; }
      .label.bleu { color: #2563eb; } .label.vert { color: #16a34a; }
      .date { font-size: 11px; color: #94a3b8; margin-top: 10px; }
      .contenu { font-size: 14px; line-height: 1.7; white-space: pre-wrap; }
      .vide { color: #94a3b8; font-style: italic; font-size: 13px; }
      .footer { margin-top: 40px; border-top: 1px solid #e2e8f0; padding-top: 12px; font-size: 11px; color: #94a3b8; text-align: center; }
      @media print { .no-print { display: none; } }
    </style></head><body>
    <h1>Notes de la semaine${pseudonyme ? ' — ' + pseudonyme : ''}</h1>
    <div class="sub">Semaine du ${new Date(semaine).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
    <div class="bloc">
      <div class="label bleu">Message des parents à l'école</div>
      ${notesParent ? `<div class="contenu">${notesParent}</div><div class="date">Envoyé le ${formatDate(dateParent)}</div>` : '<div class="vide">Aucun message.</div>'}
    </div>
    <div class="bloc">
      <div class="label vert">Message de l'école aux parents</div>
      ${notesEcole ? `<div class="contenu">${notesEcole}</div><div class="date">Envoyé le ${formatDate(dateEcole)}</div>` : '<div class="vide">Aucun message.</div>'}
    </div>
    <div class="footer">KudoGenius — offert par NutryGenius®</div>
    <script>window.onload = () => window.print()</script>
    </body></html>`
    const blob = new Blob([html], { type: 'text/html' })
    window.open(URL.createObjectURL(blob), '_blank')
  }

  const titreMonBloc = estEcole ? "Message de l'école aux parents" : "Message des parents à l'école"
  const titreAutreBloc = estEcole ? "Message des parents à l'école" : "Message de l'école aux parents"
  const colorMoi = estEcole ? '#16a34a' : '#2563eb'
  const colorAutre = estEcole ? '#2563eb' : '#16a34a'

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 3, height: 18, background: '#64748b', borderRadius: 2 }} />
          <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0 }}>
            Communication école — parents
          </h3>
        </div>
        <button onClick={imprimer} style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter',sans-serif", display: 'flex', alignItems: 'center', gap: 4 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6z"/></svg>
          Imprimer
        </button>
      </div>

      {/* MON MESSAGE */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: colorMoi, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
          {titreMonBloc}
        </div>

        {/* Message envoyé — style messagerie */}
        {maNoteEnvoyee && !enModeEdition && (
          <div style={{ background: '#f1f5f9', borderRadius: '12px 12px 4px 12px', padding: '12px 16px', marginBottom: 8, position: 'relative' }}>
            <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
              {maNoteEnvoyee}
            </p>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Envoyé le {formatDate(maDateMaj)}</span>
              <button onClick={() => { setEnModeEdition(true); setMaNote(maNoteEnvoyee) }}
                style={{ fontSize: 10, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inter',sans-serif", textDecoration: 'underline', padding: 0 }}>
                Modifier
              </button>
            </div>
          </div>
        )}

        {/* Zone de saisie */}
        {enModeEdition && (
          <div>
            <textarea value={maNote} onChange={e => setMaNote(e.target.value)}
              placeholder={estEcole
                ? "Observations, points positifs, événements de la semaine..."
                : "Informations utiles, contexte, questions pour l'équipe..."}
              rows={3} autoFocus
              style={{ width: '100%', border: `1.5px solid ${colorMoi}40`, borderRadius: 10, padding: '10px 12px', fontSize: 13, fontFamily: "'Inter', sans-serif", resize: 'vertical', outline: 'none', color: '#1e293b', lineHeight: 1.6, boxSizing: 'border-box' }}
              onFocus={e => e.target.style.borderColor = colorMoi}
              onBlur={e => e.target.style.borderColor = `${colorMoi}40`} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button onClick={sauvegarder} disabled={saving || !maNote.trim()} style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: maNote.trim() ? colorMoi : '#e2e8f0', color: maNote.trim() ? '#fff' : '#94a3b8', fontSize: 12, fontWeight: 600, cursor: maNote.trim() ? 'pointer' : 'default', fontFamily: "'Inter', sans-serif" }}>
                {saving ? 'Envoi...' : 'Envoyer'}
              </button>
              {maNoteEnvoyee && (
                <button onClick={() => setEnModeEdition(false)} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
                  Annuler
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* MESSAGE REÇU — style messagerie */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: colorAutre, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
          {titreAutreBloc}
        </div>
        {noteAutre ? (
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px 12px 12px 12px', padding: '12px 16px' }}>
            <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>{noteAutre}</p>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 8 }}>
              Reçu le {formatDate(autreDateMaj)}
            </div>
          </div>
        ) : (
          <div style={{ background: '#f8fafc', border: '1px dashed #e2e8f0', borderRadius: 10, padding: '12px 16px' }}>
            <p style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic', margin: 0 }}>Aucun message pour cette semaine.</p>
          </div>
        )}
      </div>
    </div>
  )
}
